import * as KatalMetrics from '@katal/metrics';
import initialMetricsPublisher from './metrics';
import qrcode from 'qrcode-generator-es6';
import { simpleTranslate, getDefaultLocale } from './localization';

/* istanbul ignore file */

const QR_CODE_TYPE_AUTO_DETECT = 0; // Let the library detect which type of QR to output based on the data.
const QR_CODE_ERROR_CORRECTION_HIGH = 'H';
const QR_CODE_CELL_SIZE = 6;
const QR_CODE_MARGIN = 2;

const REMOVE_AFTER_CHAR_LIST = ['?', '#', ';'];

const defaultCountryAmazonURL = new Map();
defaultCountryAmazonURL.set('AE', '.sa');
defaultCountryAmazonURL.set('CZ', '.de');
defaultCountryAmazonURL.set('DE', '.de');
defaultCountryAmazonURL.set('AU', '.com.au');
defaultCountryAmazonURL.set('CA', '.ca');
defaultCountryAmazonURL.set('GB', '.co.uk');
defaultCountryAmazonURL.set('IN', '.in');
defaultCountryAmazonURL.set('SG', '.cn');
defaultCountryAmazonURL.set('US', '.com');
defaultCountryAmazonURL.set('CO', '.com');
defaultCountryAmazonURL.set('ES', '.es');
defaultCountryAmazonURL.set('MX', '.com.mx');
defaultCountryAmazonURL.set('FR', '.fr');
defaultCountryAmazonURL.set('IL', '.com');
defaultCountryAmazonURL.set('IT', '.it');
defaultCountryAmazonURL.set('JP', '.co.jp');
defaultCountryAmazonURL.set('KR', '.com');
defaultCountryAmazonURL.set('NL', '.nl');
defaultCountryAmazonURL.set('PL', '.pl');
defaultCountryAmazonURL.set('BR', '.com.br');
defaultCountryAmazonURL.set('PT', '.es');
defaultCountryAmazonURL.set('SE', '.se');
defaultCountryAmazonURL.set('TR', '.de');
defaultCountryAmazonURL.set('CN', '.cn');
defaultCountryAmazonURL.set('TW', '.com');

class KatalApp {
    private isInitialized = false;
    private initializationMetric = new KatalMetrics.Metric.Initialization().withMonitor();
    private initializationMetricsPublisher = initialMetricsPublisher.newChildActionPublisherForInitialization();

    run() {
        // Select which view we show based on
        // the browser's user agent.
        if (this.isMobile()) {
            this.setupMobileView();
        } else {
            this.setupDesktopView();
        }
    }

    translationInitialized(success: boolean) {
      // This is the only we have to wait on for initialization,
      // so once it's done we're fully initialized
      this.initialized(success);
    }

    initialized(success: boolean) {
        if (this.isInitialized) {
            return;
        }
        this.isInitialized = true;
        console.log(`Initialized, success=${success}`);

        const wait = document.querySelector('.wait');
        if (wait != null && wait.parentNode != null) {
            wait!.parentNode!.removeChild(wait!);
        }

        let localeSelection = document.getElementById('localeSelection');
        if (localeSelection) {
            localeSelection.classList.remove('hidden');
        }
        if (document != null && document.getElementById('section1') != null) {
            document.getElementById('section1')!.classList.remove('hidden');
        }

        this.initializationMetric.setFailure(!success);
        this.initializationMetricsPublisher.publish(this.initializationMetric);
    }

    isMobile(): boolean {
        return /Android/i.test(navigator.userAgent)
            || /webOS/i.test(navigator.userAgent)
            || /iPhone/i.test(navigator.userAgent)
            || /iPad/i.test(navigator.userAgent)
            || /iPod/i.test(navigator.userAgent);
    }

    setupMobileView() {
        let mobileViewContainer = document.getElementById('ar-page-container-mobile');
        if (mobileViewContainer) {
            mobileViewContainer.classList.remove('ar-hidden');
        }

        let continueButton = document.getElementById("ar-continue-button") as HTMLLinkElement;
        let downloadAppButton = document.getElementById("ar-app-store-link-download-now") as HTMLLinkElement;

        // Set up the URL in the text box and the download button.
        if (continueButton && downloadAppButton) {
            // Use encodeURI to protect against XSS vulnerabilities
            // If we split by the slash character, the routine id will
            // be the nth element:  (0) https: / (1) / (2)alexa.amazon.com / (3) routines / (4) shared / (5) id
            let routineId = encodeURI(window.location.href).split('/')[5];
            routineId = this.removeAfterCharacter(routineId);
            let longRoutineUrl = "https://alexa.amazon.com/spa/routines.html#v2/behaviors/sharedRoutine?routineType=shared&behaviorId="
                + routineId;
            if (this.isAndroid()) {
                downloadAppButton.href = "market://details?id=com.amazon.dee.app";
                continueButton.href = longRoutineUrl;
            } else if (this.isAmazonSilk()) {
                downloadAppButton.href = "amzn://apps/android?asin=B00P03D4D2";
                continueButton.href = longRoutineUrl;
            } else {
                downloadAppButton.href = "https://itunes.apple.com/us/app/amazon-alexa/id944011620?ls=1&mt=8";
                continueButton.href = "alexa://routine#v2/behaviors/sharedRoutine?routineType=shared&behaviorId="
                    + routineId;
            }

        }

        // Set up the copy function
        this.setupCopyUrl();
    }

    setupDesktopView() {
        var desktopViewContainer = document.getElementById('ar-page-container');
        if (desktopViewContainer) {
            desktopViewContainer.classList.remove('ar-hidden');

            this.createQrCode();
            this.setupHelpLink();
        }
    }

    createQrCode() {
        var routineUrl = this.sanatizeUrl(window.location.href);

        const qr = new qrcode(QR_CODE_TYPE_AUTO_DETECT, QR_CODE_ERROR_CORRECTION_HIGH);
        qr.addData(routineUrl);
        qr.make();

        var qrcodeDiv = document.getElementById('ar-qr-code-container');
        if(qrcodeDiv) {
            qrcodeDiv.innerHTML = qr.createImgTag(QR_CODE_CELL_SIZE, QR_CODE_MARGIN);
        }
    }

    setupCopyUrl() {
        let copyText = document.getElementById("ar-copy-link-url-text") as HTMLInputElement;
        let copyButton = document.getElementById("ar-copy-link-widget-button") as HTMLButtonElement;

        /* Select the text field */
        if (copyText && copyButton) {
            copyText.value = this.sanatizeUrl(window.location.href);
            copyButton.onclick = function () {
                copyText.select();
                copyText.setSelectionRange(0, 99999); /*For mobile devices*/

                /* Copy the text inside the text field */
                document.execCommand("copy");

                /* Alert the copied text */
                alert(simpleTranslate('ar-copied-text') + copyText.value);
            }
        }
    }

    setupHelpLink() {
        let linkText = document.getElementById("ar-link-to-help-page") as HTMLLinkElement;
        
        if(linkText) {
            const helpLinkURLPrefix = 'https://www.amazon';
            const helpLinkURLSuffex = '/gp/help/customer/display.html?nodeId=G4EXU2KRU46N6E3M';
            const language: string = navigator.language;
            const extenstion = this.getAmazonWebsiteExtenstion(language);

            linkText.href = helpLinkURLPrefix + extenstion + helpLinkURLSuffex;
        }

    }

    /**
     * This class returns the extenstion of the country code for the given language,
     * the default country for the given language, or the the US extenstion if no
     * accpetable country code can be found.
     * 
     * @param givenLanguage Locale string used to determin country.
     * @returns Extenstion string for completing the help link URL.
     */
    getAmazonWebsiteExtenstion(givenLanguage: string): string {
        if (givenLanguage && givenLanguage.length > 0) {
            let language = givenLanguage;
            // if the given language has a contry code provided (en-*US*).
            if (language.length > 2) {
                const countryCode = this.getCountryCode(language);
                const extenstion = defaultCountryAmazonURL.get(countryCode);
                if (extenstion) {
                    return extenstion;
                }
            }
            // If the given locale string does not have a country code (en)
            // or the given country code does not have an extention hard coded (de-LI)
            // get the default locale for the given language.
            const defaultLocale = getDefaultLocale(language.substring(0, 2));
            const countryCode = this.getCountryCode(defaultLocale);
            const extenstion = defaultCountryAmazonURL.get(countryCode);
            if (extenstion) {
                return extenstion;
            }
        }
        return defaultCountryAmazonURL.get('US');
    }

    getCountryCode(locale: string): string {
        if (locale.length > 2) {
            return locale.substring(3);
        }
        return '';
    }

    isAmazonSilk(): boolean {
        // http://docs.aws.amazon.com/silk/latest/developerguide/user-agent.html
        return ((/Silk/).test(navigator.userAgent) && !(/Playstation/i).test(navigator.userAgent));
    }

    isIOS(): boolean {
        return (/iP(hone|ad|od)/).test(navigator.userAgent);
    }

    isAndroid(): boolean {
        return (/Android/).test(navigator.userAgent)
    }

    // This removes any string in the id that comes after specified characters.
    // This is to prevent websites from adding extra parts to routine ids when shared.
    removeAfterCharacter(id: string): string {
        REMOVE_AFTER_CHAR_LIST.forEach((char: string) => {
            id = id.split(char)[0];
        });
        return id;
    }

    // Splits URL into parts, then removes the unwanted parts of the routine id,
    // then rebuilds the url with the sanatized id.
    sanatizeUrl(originalUrl: string): string {
        let routineUrl = encodeURI(originalUrl).split('/');
        let routineid = routineUrl[5];
        routineid = this.removeAfterCharacter(routineid);
        return originalUrl.replace(routineUrl[5], routineid);
    }
}

const app = new KatalApp();
export default app;
